import React from 'react';
import Container from '../../components/Container';
import {UserContext} from '../../UserContext';
import {api} from '../../services/api';
import {GridColDef, GridRowData, GridToolbarDensitySelector, GridToolbarFilterButton} from '@material-ui/data-grid';
import Table from '../../components/Table';
import {createTheme, createStyles, makeStyles, Theme} from '@material-ui/core';
import Select from '../../components/Form/Select';
import { UserPendingProps } from '../../interfaces';
import Alert from '../../components/Alert';
import {default as MaterialUiAlert} from '@material-ui/lab/Alert';
  
const defaultTheme = createTheme();
const useStyles = makeStyles(
(theme: Theme) =>
    createStyles({
        root: {
            padding: theme.spacing(0.5, 0.5, 0.5),
            justifyContent: 'space-between',
            display: 'flex',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
        },        
        }),
    { defaultTheme },
);

interface QuickSearchToolbarProps {
    clearSearch: () => void;
    onChange: () => void;
    value: string;
}

function QuickSearchToolbar(props: QuickSearchToolbarProps) {
    const classes = useStyles();

    return (
        <div className={classes.root}>
        <div>
            <GridToolbarFilterButton />
            <GridToolbarDensitySelector />
        </div>        
        </div>
    );
}

const groups = [
    {id: 100, description: 'Administrador'},
    {id: 101, description: 'ADV'},
    {id: 102, description: 'Cliente'},
    {id: 103, description: 'DC'},
    {id: 104, description: 'Representante'},
    {id: 105, description: 'Suporte'},
    {id: 106, description: 'Visitante'},
    {id: 107, description: 'Cliente+DA'},
    {id: 108, description: 'Cobrança'},
    {id: 109, description: 'Transportadora'},
];

const UserPending: React.FC = () => {
    document.title = "Usuários Pendentes - Sultan Veste a Casa";

    const [rows, setRows] = React.useState<GridRowData[]>([]);
    const [error, setError] = React.useState("");
    const [success, setSuccess] = React.useState("");

    const {setLoading} = React.useContext(UserContext);

    React.useEffect(() => {
        getUsers();
        // eslint-disable-next-line
    }, []);

    const deleteUser = async(id: number) => {
        try{
            setLoading(true);

            const {data} = await api.delete(`/user-pending/${id}`);
            setSuccess(data.message);
        }catch(error){
            const result = (error as Error).message;
            setError(result);
        }finally{
            setLoading(false);
            getUsers();
        }
    }

    const getUsers = async() => {
        try{
            setRows([]);
            setLoading(true);

            const {data} = await api.get('/user-pending');
            
            const users = data.users.map((el: UserPendingProps) => {
                return {
                    id: el.id,
                    col1: el.id,
                    col2: el.sNome,
                    col3: el.sEmail,
                    col4: el.sCPF,  
                    col5: el.PKGrupo,
                    col6: el.cellphone,
                    col7: el.phone,
                    col9: 'Aprovar',
                    col10: 'Deletar', 
                    col11: el.PKGrupo,       
                };
            });

            setRows(users);
        }catch(error){
            const result = (error as Error).message;
            setError(result);
        }finally{
            setLoading(false);
        }
    }

    const changeGroup = async (value: string, id: number) => {
        try{
            setRows([]);
            setLoading(true);

            const {data} = await api.put(`/user-pending/${id}/${value}`);
            
            setSuccess(data.message);
        }catch(error){
            const result = (error as Error).message;
            setError(result);
        }finally{
            setLoading(false);
            getUsers();
        }
    }

    const approvedUser = async (id: number) => {
        try{
            setRows([]);
            setLoading(true);
            
            const {data} = await api.post(`/user`, {id});

            setSuccess(data.message);
        }catch(error){
            const result = (error as Error).message;
            setError(result);
        }finally{
            setLoading(false);
            getUsers();
        }
    }

    const handleMessageSuccess = () => {
        setSuccess('');
    }

    const handleMessageError = () => {
        setError('');
    }

    const columns: GridColDef[] = [
        {field: 'col1', headerName: 'ID', width: 100},
        {field: 'col2', headerName: 'Nome', width: 170},
        {field: 'col3', headerName: 'E-mail', width: 170},
        {field: 'col4', headerName: 'CPF/CNPJ', width: 170,},
        {field: 'col5', headerName: 'Grupo', width: 200, renderCell: (params) => {
            return (
                <div className="d-flex justify-content-between align-items-center" style={{cursor: "pointer"}}>
                    <Select style={{margin: 0}} id="group" value={params.row.col11} onChange={(event) => changeGroup(event.target.value, params.row.col1)} options={groups}/>
                </div>
            );
        }},       
        {field: 'col6', headerName: 'Celular', width: 150},
        {field: 'col7', headerName: 'Telefone', width: 150},
        {field: 'col8', headerName: 'Termos', width: 120},
        {field: 'col9', headerName: 'Ação', width: 120, renderCell: (params) => {
            return (
                <div className="d-flex justify-content-between align-items-center" style={{cursor: "pointer"}}>
                    <span className="link-default" onClick={() => approvedUser(params.row.col1)}>Aprovar</span>
                </div>
            );
        }},
        {field: 'col10', headerName: 'Ação', width: 120, renderCell: (params) => {
            return (
                <div className="d-flex justify-content-between align-items-center" style={{cursor: "pointer"}}>
                    <span className="link-warning" onClick={() => deleteUser(params.row.col1)}>Deletar</span>
                </div>
            );
        }},
        {field: 'col11', headerName: 'PK', width: 150, hide: true},
    ];
 
    return (
        <Container>
            <MaterialUiAlert severity="info">
                Você encontrará aqui os usuários que necessitam de aprovação para acessar o sistema.
            </MaterialUiAlert>
            <Table rows={rows} columns={columns} components={{ Toolbar: QuickSearchToolbar }}/>
            <Alert open={error !== ''} onClose={handleMessageError} severity="error" message={error}/>                    
            <Alert open={success !== ''} onClose={handleMessageSuccess} severity="success" message={success}/> 
        </Container>
    )
}

export default UserPending;
